import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Loader2, AlertTriangle, Info, RotateCcw } from 'lucide-react';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';
import { useEngineAnalysis } from '@/hooks/useEngineAnalysis';
import { trackProductEvent } from '@/lib/analytics';
import { useSustainedView } from '@/lib/calculatorAnalytics';
import { formatEngineVersion } from '@/lib/engineVersion';

function YearBlock({ year, formatCost }) {
  const { t } = useTranslation();
  const dayLabel = t('tco.daysSuffix');

  return (
    <div className="border border-[#DDE1E5] rounded-lg bg-white" data-testid={`tco-year-${year.year}`}>
      <div className="px-4 py-3 border-b border-[#DDE1E5]">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
             style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {t('tco.yearLabel', { year: year.year })}
        </div>
        <div className="mt-0.5 text-sm font-medium text-[#171A1D]">
          {year.phase === 'implementation' ? t('tco.phaseImplementation') : t('tco.phaseOperation')}
        </div>
      </div>
      <dl x-excluded="true" className="px-4 py-2 divide-y divide-[#DDE1E5]">
        {year.components.map(component => (
          <div
            key={component.id}
            className="py-2 flex items-baseline justify-between gap-3"
            data-testid={`tco-y${year.year}-${component.id}`}
          >
            <dt className="text-xs text-[#3A4249] min-w-0">
              {t(`tco.components.${component.id}`)}
              {component.days ? (
                <span className="ml-1.5 text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {component.days}{dayLabel}
                </span>
              ) : null}
            </dt>
            <dd className="text-xs text-[#171A1D] shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {formatCost(component.amount)}
            </dd>
          </div>
        ))}
      </dl>
      <div className="px-4 py-3 border-t border-[#DDE1E5] flex items-baseline justify-between gap-3 bg-[#F5F6F7] rounded-b-lg">
        <span className="text-xs font-medium text-[#3A4249]">{t('tco.yearTotal')}</span>
        <span className="text-sm font-semibold text-[#171A1D]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid={`tco-year-total-${year.year}`}>
          {formatCost(year.total)}
        </span>
      </div>
    </div>
  );
}

function AssumptionControls({ assumptions, overrides, onChange }) {
  const { t } = useTranslation();

  const update = (id, raw) => {
    const next = { ...overrides };
    if (raw === '' || raw === null) delete next[id];
    else next[id] = Number(raw);
    onChange(next);
  };

  return (
    <div className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="tco-assumptions">
      <div className="px-4 py-3 border-b border-[#DDE1E5]">
        <h3 className="text-sm font-medium text-[#171A1D]">{t('tco.assumptionsTitle')}</h3>
        <p className="mt-1 text-xs text-[#66707A]">{t('tco.assumptionsIntro')}</p>
      </div>
      <div className="px-4 py-2 divide-y divide-[#DDE1E5]">
        {assumptions.map(assumption => (
          <div
            key={assumption.id}
            className="py-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2"
            data-testid={`tco-assumption-${assumption.id}`}
          >
            <div className="min-w-0">
              <div className="text-xs text-[#3A4249]">{t(`tco.assumptions.${assumption.id}`)}</div>
              <div className="text-[11px] text-[#8A929B]">{t(`tco.units.${assumption.unit}`)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="number"
                value={overrides[assumption.id] ?? assumption.value}
                min={assumption.min}
                max={assumption.max}
                step={assumption.unit === 'days' ? 1 : 5}
                onChange={(e) => update(assumption.id, e.target.value)}
                className="h-8 w-20 rounded-[6px] border border-[#DDE1E5] bg-white px-2 text-sm text-[#171A1D] focus:border-[#78B928] outline-none"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid={`tco-input-${assumption.id}`}
                aria-label={t(`tco.assumptions.${assumption.id}`)}
              />
              <ProvenanceBadge provenance={assumption.provenance} />
              {overrides[assumption.id] != null && (
                <button
                  type="button"
                  onClick={() => update(assumption.id, '')}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7] transition-colors duration-150"
                  data-testid={`tco-reset-${assumption.id}`}
                  aria-label={t('assumptions.resetDefault')}
                  title={t('assumptions.resetDefault')}
                >
                  <RotateCcw className="w-3 h-3" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 3-Year Planning Outlook: Year 1 implementation + certification (the
 * authoritative estimate) plus two modelled years of ISMS operation.
 * Recertification is explicitly reported as excluded, never silently omitted.
 */
export function TcoPanel({ config, overrides = {}, onOverridesChange, onData, formatCost }) {
  const { t } = useTranslation();
  const { data, loading, error, retry } = useEngineAnalysis(
    '/estimates/tco', { config, tco: overrides }, { delay: 450 },
  );

  useEffect(() => { if (data && onData) onData(data); }, [data, onData]);

  // Outlook engagement, recorded once per calculator mount, on whichever comes
  // first: the user overriding a recurring assumption, or the outlook header
  // staying in view for two uninterrupted seconds with authoritative data
  // loaded. Rendering alone is never engagement.
  const headerRef = useRef(null);
  const engaged = useRef(false);
  const markEngaged = useCallback((interaction) => {
    if (engaged.current) return;
    engaged.current = true;
    trackProductEvent('tco_engaged', { source: 'calculator', interaction });
  }, []);
  const onSustained = useCallback(() => markEngaged('sustained_view'), [markEngaged]);
  useSustainedView(headerRef, { enabled: Boolean(data), onSustained });

  return (
    <section className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="tco-panel">
      <div className="px-5 py-4 border-b border-[#DDE1E5] flex flex-wrap items-start justify-between gap-3"
           ref={headerRef} data-testid="tco-header">
        <div className="min-w-0 flex items-start gap-2.5">
          <CalendarRange className="w-4 h-4 text-[#66707A] mt-1 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <h2 className="text-lg font-medium tracking-tight text-[#171A1D]" data-testid="tco-title">
              {t('tco.title')}
            </h2>
            <p className="mt-0.5 text-sm text-[#66707A]">{t('tco.subtitle')}</p>
          </div>
        </div>
        {data && (
          <div className="text-right shrink-0">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
                 style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {t('tco.total')}
            </div>
            <div className="text-2xl font-semibold text-[#171A1D]"
                 style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                 data-testid="tco-total">
              {formatCost(data.total)}
            </div>
          </div>
        )}
      </div>

      <div className="p-5">
        {loading && !data && (
          <div className="flex items-center gap-2 py-4 text-sm text-[#66707A]" data-testid="tco-loading">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('tco.loading')}
          </div>
        )}

        {error && (
          <div className="py-4" data-testid="tco-error">
            <p className="flex items-center gap-2 text-sm text-[#B42318]">
              <AlertTriangle className="w-4 h-4" aria-hidden="true" /> {t('tco.error')}
            </p>
            <button
              type="button"
              onClick={retry}
              className="mt-2 text-xs font-medium text-[#527F19] hover:text-[#3F6114]"
              data-testid="tco-retry"
            >
              {t('sensitivity.retry')}
            </button>
          </div>
        )}

        {data && (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              {data.years.map(year => (
                <YearBlock key={year.year} year={year} formatCost={formatCost} />
              ))}
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1fr]">
              <AssumptionControls
                assumptions={data.assumptions}
                overrides={overrides}
                onChange={(next) => { markEngaged('assumption_override'); onOverridesChange(next); }}
              />

              <div className="space-y-3">
                <div className="border border-[#DDE1E5] rounded-lg bg-[#F5F6F7] px-4 py-3">
                  <div className="flex items-baseline justify-between gap-3 py-1">
                    <span className="text-xs text-[#3A4249]">{t('tco.year1Total')}</span>
                    <span className="text-sm text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                          data-testid="tco-year1-total">
                      {formatCost(data.year1Total)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1 border-t border-[#DDE1E5]">
                    <span className="text-xs text-[#3A4249]">{t('tco.recurringTotal')}</span>
                    <span className="text-sm text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                          data-testid="tco-recurring-total">
                      {formatCost(data.recurringTotal)}
                    </span>
                  </div>
                </div>

                <div className="border border-[#B7791F]/35 bg-[#B7791F]/[0.06] rounded-lg px-4 py-3"
                     data-testid="tco-recertification-note">
                  <div className="flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 text-[#B7791F] mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <div className="text-xs font-medium text-[#8A5A0B]">{t('tco.recertTitle')}</div>
                      <p className="mt-0.5 text-xs text-[#8A5A0B]">{t('tco.recertExcluded')}</p>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-[#8A929B]" data-testid="tco-surveillance-note">
                  {t('tco.surveillanceNote')}
                </p>
                <p className="text-xs text-[#8A929B]">{t('tco.note')}</p>
                <p className="text-[11px] text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                   data-testid="tco-model-version">
                  {t('tco.modelVersion')} {formatEngineVersion(data.tcoModelVersion)} · {t('trust.engineVersion')} {formatEngineVersion(data.engineVersion)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
